import { IDocumentMetadata } from "@multiweb/types/contracts";
import { InvalidOperationException } from "../../Global/Base/Exceptions";
import { IStreamDecryptor } from "../../Global/Base/Security/IStreamDecryptor";
import { SecurityUtils } from "../../Global/Base/Security/SecurityUtils";
import { ISignatureDataStream } from "../Crypto/CryptoInterfaces";

/**
 * Stream poskytující dešifrovaná data pro vytváření podpisu.
 */
export class EncryptedDocumentSignatureStream implements ISignatureDataStream {
    private readonly _decryptor: IStreamDecryptor;
    private readonly _stream: ISignatureDataStream;

    /**
     * Vytvoří nový stream poskytující dešifrovaná data dokumentu z DMS a aplikační služby
     * @param wrappedSteam Stream zašifrovaných dat
     * @param key Klíč pro dešifrování
     */
    constructor(wrappedSteam: ISignatureDataStream, key: CryptoKey) {
        if (!key)
            throw new InvalidOperationException("Unable to download encrypted file without key");

        this._stream = wrappedSteam;
        this._decryptor = SecurityUtils.getStreamDecryptor(key);
        if (!this._decryptor)
            throw new InvalidOperationException(`Unable to find decryptor for key '${key.algorithm.name}'`); //`
    }

    /**
     * Vygeneruje URI pro referenci ukazující na data v podpisu
     */
    public async getReferenceUri(): Promise<string> {
        return this._stream.getReferenceUri();
    }

    /**
     * Načte název dat
     * @returns Název dat
     */
    public async getName(): Promise<string> {
        return this._stream.getName();
    }

    /**
     * Načítá veškerá metadata ve formátu klíč - hodnota
     * @returns Metadata
     */
    public async getMetadata(): Promise<IDocumentMetadata | null> {
        return this._stream.getMetadata();
    }
    /**
     * Načítá celý obsah dokumentu. Stahuje po chuncích definované délky. Po stažení prvního zároveň odesílá
     * první chunk nativní aplikaci a stahuje druhý chunk, atd.
     * @param chunkSize Velikost, po které se mají data načítat
     * @param onChunkDownloaded Callback funkce, která je volaná při načtení části dat
     * @returns Data dokumentu
     */
    public async read(chunkSize: number, onChunkDownloaded: (chunk: ArrayBuffer, isLastChunk: boolean) => Promise<void>): Promise<void> {
        await this._stream.read(chunkSize, async (chunk: ArrayBuffer, isLastChunk: boolean) => {
            onChunkDownloaded(await this._decryptor.decryptBlock(chunk, isLastChunk), isLastChunk);
        });
    }
}
